/**
 * Utilitaires pour la validation des routes
 */

// Liste des routes valides exactes
const VALID_EXACT_ROUTES = [
  '/',
  '/dashboard',
  '/stats',
  '/payment/new',
  '/payment/organisation',
  '/payment/other/new',
  '/payment/list',
  '/payment/rejected/list',
  '/payment/autoriser/list',
  '/payment/other/list',
  '/payment/historique/list',
  '/payment/organisation/list',
  '/resubmission/other/new',
  '/users/list',
  '/users/roles/list',
  '/agencies/list',
  '/wards/list',
  '/myaccount/settings',
  '/certificatperte/nouveau',
  '/certificatperte/list',
  '/ct/paiement/nouveau',
  '/ct/paiement/list',
  '/ct/paiement/statistics',
  '/cmc/nouveau',
  '/cmc/listpaiement',
  '/cmc/douane/listecharge',
  '/cmc/statistiques/parjourcategorie',
  '/cmc/statistiques/parcategorie',
  '/organisation/list',
  '/apc/nouvelle',
  '/apc/liste',
  '/apc/edition',
  '/apc/attestation/nouvelle',
  '/apc/attestation/liste'
];

// Patterns des routes avec paramètres dynamiques
const DYNAMIC_ROUTE_PATTERNS = [
  /^\/payment\/invoice\/[^\/]+$/,
  /^\/payment\/invoice\/organisation\/[^\/]+$/,
  /^\/resubmission\/new\/[^\/]+$/,
  /^\/certificatperte\/invoicedetails\/[^\/]+$/,
  /^\/ct\/paiement\/facture\/[^\/]+$/,
  /^\/cmc\/details\/[^\/]+$/,
  /^\/apc\/invoice\/[^\/]+$/,
  /^\/apc\/attestation\/print\/[^\/]+(?:\/[^\/]+)?$/
];

// Routes spéciales qui nécessitent un traitement particulier
const SPECIAL_ROUTES = [
  '/douane/consultationcms/liste'
];

// Routes publiques accessibles sans authentification
const PUBLIC_ROUTE_SEGMENTS = [
  '/apc/invoice',
  '/apc/attestation/print',
  '/privacy-policy',
  '/terms-of-use'
];

// Routes qui contiennent des segments à exclure de la navigation normale
const EXCLUDED_ROUTE_SEGMENTS = [
  '/apc/attestation/print'
];

/**
 * Vérifie si une route est valide
 * @param {string} pathname - Le chemin de la route à vérifier
 * @returns {boolean} - True si la route est valide, false sinon
 */
export const isValidRoute = (pathname) => {
  // Vérifier les routes exactes
  if (VALID_EXACT_ROUTES.includes(pathname)) {
    return true;
  }
  
  // Vérifier les routes avec paramètres dynamiques
  return DYNAMIC_ROUTE_PATTERNS.some(pattern => pattern.test(pathname));
};

/**
 * Vérifie si une route est une route spéciale
 * @param {string} pathname - Le chemin de la route à vérifier
 * @returns {boolean} - True si c'est une route spéciale
 */
export const isSpecialRoute = (pathname) => {
   return SPECIAL_ROUTES.includes(pathname);
};

/**
 * Vérifie si une route est publique (accessible sans authentification)
 * @param {string} pathname - Le chemin de la route à vérifier
 * @returns {boolean} - True si c'est une route publique
 */
export const isPublicRoute = (pathname) => {
  return PUBLIC_ROUTE_SEGMENTS.some(segment => pathname.includes(segment));
};

/**
 * Vérifie si une route contient des segments exclus
 * @param {string} pathname - Le chemin de la route à vérifier
 * @returns {boolean} - True si la route contient des segments exclus
 */
export const hasExcludedSegments = (pathname) => {
  return EXCLUDED_ROUTE_SEGMENTS.some(segment => pathname.includes(segment));
};

/**
 * Détermine si la navbar et le sidemenu doivent être affichés
 * @param {string} pathname - Le chemin de la route actuelle
 * @returns {boolean} - True si navbar/sidemenu doivent être affichés
 */
export const shouldShowNavigation = (pathname) => {
  // Ne pas afficher la navigation pour les routes spéciales
  if (isSpecialRoute(pathname)) {
    return false;
  }
  
  // Ne pas afficher la navigation pour les routes publiques
  if (isPublicRoute(pathname)) {
    return false;
  }
  
  // Ne pas afficher la navigation pour les routes avec segments exclus
  if (hasExcludedSegments(pathname)) {
    return false;
  }
  
  // Afficher la navigation seulement pour les routes valides
  return isValidRoute(pathname);
};

/**
 * Détermine si le contenu principal (avec UserContext) doit être affiché
 * @param {string} pathname - Le chemin de la route actuelle
 * @returns {boolean} - True si le contenu principal doit être affiché
 */
export const shouldShowMainContent = (pathname) => {
  // Ne pas afficher le contenu principal pour les routes spéciales
  if (isSpecialRoute(pathname)) {
    return false;
  }
  
  // Ne pas afficher le contenu principal pour les routes publiques
  if (isPublicRoute(pathname)) {
    return false;
  }
  
  // Ne pas afficher le contenu principal pour les routes avec segments exclus
  if (hasExcludedSegments(pathname)) {
    return false;
  }
  
  return true;
};

/**
 * Obtient le type de rendu nécessaire pour une route
 * @param {string} pathname - Le chemin de la route actuelle
 * @returns {string} - 'main' | 'special' | 'print' | 'public'
 */
export const getRouteRenderType = (pathname) => {
  if (isSpecialRoute(pathname)) {
    return 'special';
  }
  
  if (isPublicRoute(pathname)) {
    return 'public';
  }
  
  if (hasExcludedSegments(pathname)) {
    return 'print';
  }
  
  return 'main';
};

/**
 * Valide et catégorise une route
 * @param {string} pathname - Le chemin de la route à analyser
 * @returns {object} - Objet contenant les informations de validation
 */
export const validateRoute = (pathname) => {
  return {
    isValid: isValidRoute(pathname),
    isSpecial: isSpecialRoute(pathname),
    isPublic: isPublicRoute(pathname),
    hasExcludedSegments: hasExcludedSegments(pathname),
    shouldShowNavigation: shouldShowNavigation(pathname),
    shouldShowMainContent: shouldShowMainContent(pathname),
    renderType: getRouteRenderType(pathname)
  };
};
